import { StanFilterOperator, StanFilterType } from '@/components/core-components/StanFilters/types';
import { GENERIC_STATUS } from '@/lib/constants';
import { PaymentData, PaymentTags, PosData } from '@/types/payments';

// Enums
export enum PaymentType {
    DIGITAL_DOWNLOAD = 'digital-download',
    LEAD_MAGNET = 'lead',
    BOOKING = 'booking',
    FULFILLMENT = 'fulfillment',
    MEMBERSHIP = 'membership',
    COURSE = 'course',
    PAYOUT = 'payout',
    REFERRAL_FEE = 'referral-fee',
    AFFILIATE_FEE = 'affiliate-fee',
    WEBINAR = 'webinar',
    TOPUP = 'topup',
    INVOICE = 'invoice',
    ACCOUNT_CHARGE = 'account-charge',
    COMMUNITY = 'community',
    RESELL = 'resell',
    AI_AVATAR = 'ai-avatar',
}

export enum PaymentStatus {
    PAYMENT_INITIALISED = 0,
    CREATOR_FULFILLED = 1,
    PAID_NOT_FULFILLED = 2,
    REFUNDED = 3,
    DISPUTED = 4,
    FAILED = 5,
    PENDING = 6,
}

export enum PaymentGateway {
    MANUAL_ACCESS_GRANT = 'manual-access-granted',
    STRIPE = 'stripe',
    STRIPE_CHECKOUT = 'stripe-checkout',
    PAYPAL = 'paypal',
    ADYEN = 'adyen',
}

// Interfaces
export interface BillingAddress {
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
}

export interface SalesTaxBreakdown {
    taxability_reason?: string;
    tax_rate_detail_tax_type?: string;
    tax_rate_detail_percentage_decimal?: string;
}

export interface AffiliateShare {
    affiliated_user_id?: number;
    resell_rights_uuid?: string;
}

export type InvestigationStatus = 'pending' | 'upheld' | 'resolved' | 'cancelled' | 'processed';

export interface PaymentOrder {
    id: number;
    amount: number;
    processing_fee: number;
    amount_base: number;
    created_at: string;
    created_at_utc_ts: number;
    type: PaymentType;
    data: PaymentData;
    pos_data: PosData;
    product_type: PaymentType;
    status: PaymentStatus;
    page_id?: number;
    revenue: number;
    payment_gateway: PaymentGateway;
    pos_currency: string;
    currency_base: string;
    full_name?: string;
    email?: string;
    user_fan_id?: number;
    user_fan_status?: (typeof GENERIC_STATUS)[keyof typeof GENERIC_STATUS];
    date: string;
    affiliated_username?: string;

    // Additional fields when show_pos_data is true
    conversion?: number;
    tax?: number;
    fee?: number;
    net?: number;
    cc_issue_country?: string;
    settlement_currency?: string;
    taxability_reason?: string;
    tax_type?: string;
    tax_rate?: string;
    billing_address?: string;
    tags?: PaymentTags;

    // Refund specific fields
    refunded_on?: string;
}

export interface PaginationOrders {
    current_page: number;
    data: PaymentOrder[];
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
}

export interface OrderSearchResponse {
    all: PaginationOrders;
}

// Extended order details response (from /api/v1/payments/orders/<id>)
export interface ExtendedPaymentOrderDetail {
    amount: number;
    pos_currency: string;
    currency: string;
    processing_fee: number;
    amount_base: number;
    currency_base: string;
    payment_gateway: PaymentGateway;
    product_type: PaymentType;
    data: PaymentData;
    pos_data: PosData;
    date: string;
    status: PaymentStatus;
    affiliated_username?: string;
    user_fan_id?: number;
    user_fan_status?: number;
    full_name?: string;
    email?: string;
    payment_plan_status?: number;
    payment_plan_data?: Record<string, any>;
    stripe_events?: Record<string, any>[];
    investigation_status?: number;
}

export interface OrderSearchRequest {
    page?: number;
    keyword?: string;
    sortBy?: string;
    filter?: string;
    filter_array?: string;
    type?: PaymentType;
    status?: PaymentStatus.CREATOR_FULFILLED | PaymentStatus.PAID_NOT_FULFILLED | PaymentStatus.REFUNDED;
    timezone?: string;
    per_page?: number;
    user_fan_id?: number;
}

export interface OrderParams {
    page?: number;
    keyword?: string;
    sortBy?: string;
    filter?: string;
    filterArray?: string;
    type?: PaymentType;
    status?: PaymentStatus.CREATOR_FULFILLED | PaymentStatus.PAID_NOT_FULFILLED | PaymentStatus.REFUNDED;
    timezone?: string;
    perPage?: number;
    userFanId?: number;
}

export interface OrderParamsRequest {
    page?: number; // default: 1
    keyword?: string;
    sortBy?: string; // lol camel case
    filter?: string;
    filter_array?: string;
    type?: string;
    status?: number; // 1-3
    timezone?: string; // default: "UTC"
    per_page?: number; // default: 15
    user_fan_id?: number;
}

export interface OrderFilterValue {
    fieldName: string;
    value: string;
    type: StanFilterType;
    operator: StanFilterOperator;
}

export interface AmountWithCurrency {
    amount: number;
    currency: string;
    amount_string: string; // Formatted amount string (e.g., "1,234.56")
    instant_payout?: boolean;
    instant_payout_max_amount?: number;
}

export interface PaymentProviderBalance {
    available: AmountWithCurrency;
    pending: AmountWithCurrency;
    in_transit: AmountWithCurrency;
}

// Currency types
export interface CurrencyInfo {
    name: string;
    symbol: string;
}

export interface CurrenciesApiResponse {
    currencies: Record<string, CurrencyInfo>;
}

export interface CreatorBalanceResponse {
    total_revenue: AmountWithCurrency;
    total_sales_revenue: AmountWithCurrency;
    total_referral_revenue: AmountWithCurrency;
    total_stripe_revenue: AmountWithCurrency;
    total_paypal_revenue: AmountWithCurrency;
    platform_balance: AmountWithCurrency;
    affiliate_balance: AmountWithCurrency;
    referral_balance: AmountWithCurrency;

    // Backward compatibility fields
    stripe_available: AmountWithCurrency;
    stripe_pending: AmountWithCurrency;
    stripe_dispute_hold: AmountWithCurrency;
    cashout_in_transit: AmountWithCurrency;

    // V2 provider-specific balances
    stripe: PaymentProviderBalance;
    adyen: PaymentProviderBalance;
}
